import React, {useState} from "react"
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import {useQuery, useMutation} from "@tanstack/react-query"

import {useAgent} from "@indebted/hooks/Agent"
import {Layout} from "@indebted/components/Layout"
import {useNotification} from "@indebted/components/Notification"
import {api} from "@indebted/api"

type AccountInput = {
	balance: number
	statementOfWorkID: string
}

type CustomerInput = {
	fullName: string
	dob: string
	email: string
	phone: string
}

const defaultCustomer: CustomerInput = {
	fullName: "Test Customer",
	dob: "1990-01-01",
	email: "test+customer@example.com",
	phone: "+61400000000",
}

const defaultAccount = (): AccountInput => ({
	balance: 1000,
	statementOfWorkID: "",
})

function CreateCustomerPage() {
	const {notification} = useNotification()
	const [agentEmail] = useAgent()

	const {
		data: clients = [],
		isLoading: clientsLoading,
		isError: clientsError,
	} = useQuery({
		queryKey: ["devTools", "clients"],
		queryFn: () => api.devTools.listClients(),
	})
	const [selectedClientID, setSelectedClientID] = useState<string | undefined>(undefined)
	const effectiveClientID = selectedClientID ?? clients[0]?.id ?? ""
	const selectedClient = clients.find((c) => c.id === effectiveClientID)
	const [customer, setCustomer] = useState<CustomerInput>({
		...defaultCustomer,
		email: agentEmail ?? defaultCustomer.email,
	})
	const [accounts, setAccounts] = useState<AccountInput[]>([defaultAccount()])
	const [expediteProcesses, setExpediteProcesses] = useState(true)
	const [simulateScrubs, setSimulateScrubs] = useState(true)

	const effectiveAccounts = accounts.map((a) => ({
		...a,
		statementOfWorkID: a.statementOfWorkID || selectedClient?.statementsOfWork[0]?.id || "",
	}))

	const {mutate: createCustomer, isPending: submitting} = useMutation({
		mutationFn: () =>
			api.devTools.createCustomer({
				clientID: effectiveClientID,
				customer,
				accounts: effectiveAccounts,
				expediteProcesses,
				simulateScrubs,
			}),
		onSuccess: () => notification.success("Customer upload queued successfully"),
		onError: () => notification.error("Failed to create customer"),
	})

	React.useEffect(() => {
		if (clientsError) {
			notification.error("Failed to load clients")
		}
	}, [clientsError, notification])

	const updateAccount = (index: number, patch: Partial<AccountInput>) => {
		setAccounts((prev) => prev.map((a, i) => (i === index ? {...a, ...patch} : a)))
	}

	const removeAccount = (index: number) => {
		setAccounts((prev) => prev.filter((_, i) => i !== index))
	}

	return (
		<Layout>
			<Box padding={2}>
				<Typography variant="h6" gutterBottom>
					Dev Tools — Create Test Customer
				</Typography>
				<Divider sx={{mb: 2}} />

				{clientsLoading ? (
					<CircularProgress size={24} />
				) : (
					<FormControl fullWidth size="small" sx={{mb: 2}}>
						<InputLabel>Client</InputLabel>
						<Select
							label="Client"
							value={effectiveClientID}
							onChange={(e) => {
								setSelectedClientID(e.target.value)
								setAccounts((prev) => prev.map((a) => ({...a, statementOfWorkID: ""})))
							}}
						>
							{clients.map((c) => (
								<MenuItem key={c.id} value={c.id}>
									{c.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				<Typography variant="subtitle2" gutterBottom>
					Customer
				</Typography>
				<Box display="flex" flexDirection="column" gap={1} mb={2}>
					<TextField
						label="Full Name"
						size="small"
						value={customer.fullName}
						onChange={(e) => setCustomer((prev) => ({...prev, fullName: e.target.value}))}
					/>
					<TextField
						label="Date of Birth"
						size="small"
						placeholder="YYYY-MM-DD"
						value={customer.dob}
						onChange={(e) => setCustomer((prev) => ({...prev, dob: e.target.value}))}
					/>
					<TextField
						label="Email"
						size="small"
						value={customer.email}
						onChange={(e) => setCustomer((prev) => ({...prev, email: e.target.value}))}
					/>
					<TextField
						label="Phone"
						size="small"
						value={customer.phone}
						onChange={(e) => setCustomer((prev) => ({...prev, phone: e.target.value}))}
					/>
				</Box>

				<Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
					<Typography variant="subtitle2">Accounts</Typography>
					<Button size="small" onClick={() => setAccounts((prev) => [...prev, defaultAccount()])}>
						+ Add account
					</Button>
				</Box>

				{accounts.map((account, index) => (
					<Box
						key={index}
						display="flex"
						flexDirection="column"
						gap={1}
						mb={2}
						pl={1}
						borderLeft="3px solid"
						borderColor="divider"
					>
						<Box display="flex" alignItems="center" justifyContent="space-between">
							<Typography variant="caption" color="text.secondary">
								Account {index + 1}
							</Typography>
							{accounts.length > 1 && (
								<IconButton size="small" onClick={() => removeAccount(index)}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							)}
						</Box>
						<TextField
							label="Balance (cents)"
							size="small"
							type="number"
							value={account.balance}
							onChange={(e) => updateAccount(index, {balance: parseInt(e.target.value, 10) || 0})}
						/>
						{selectedClient && selectedClient.statementsOfWork.length > 0 && (
							<FormControl fullWidth size="small">
								<InputLabel>Statement of Work</InputLabel>
								<Select
									label="Statement of Work"
									value={account.statementOfWorkID || selectedClient.statementsOfWork[0]?.id || ""}
									onChange={(e) => updateAccount(index, {statementOfWorkID: e.target.value})}
								>
									{selectedClient.statementsOfWork.map((sow) => (
										<MenuItem key={sow.id} value={sow.id}>
											{sow.productType} ({sow.id.slice(0, 8)}…)
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					</Box>
				))}

				<Box mb={2}>
					<FormControlLabel
						control={
							<Checkbox
								checked={expediteProcesses}
								onChange={(e) => setExpediteProcesses(e.target.checked)}
								size="small"
							/>
						}
						label={
							<Box>
								<Typography variant="body2">Expedite processes</Typography>
								<Typography variant="caption" color="text.secondary">
									Skips NOA wait and email/phone scrubs — schedules comms immediately
								</Typography>
							</Box>
						}
					/>
				</Box>

				<Box mb={2}>
					<FormControlLabel
						control={
							<Checkbox
								checked={simulateScrubs}
								onChange={(e) => setSimulateScrubs(e.target.checked)}
								size="small"
							/>
						}
						label={
							<Box>
								<Typography variant="body2">Simulate scrubs</Typography>
								<Typography variant="caption" color="text.secondary">
									Writes &quot;not found&quot; results for bankruptcy, deceased, and service members
									scrubs
								</Typography>
							</Box>
						}
					/>
				</Box>

				<Button
					variant="contained"
					fullWidth
					disabled={submitting || !effectiveClientID}
					onClick={() => createCustomer()}
					startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
				>
					{submitting ? "Uploading..." : "Create Customer & Accounts"}
				</Button>
			</Box>
		</Layout>
	)
}

export {CreateCustomerPage}
